import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, switchMap } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { PlanLevel } from '@ninety/platform/models/billing/plan-level';
import { BillingTypes } from '@ninety/platform/models/company/billing-types.enum';
import { BillingV3Service } from '@ninety/platform/services/services/billing-v3.service';

import { selectCurrentUserAsCompanyUser } from '../../app-entities/users/users-state.selectors';
import { SegmentActions } from '../analytics/segment/segment.actions';
import { CompanyActions } from '../company/company-state.actions';
import { SubscriptionActions } from '../company/subscription/subscription-state.actions';
import { selectSubscription } from '../company/subscription/subscription-state.selectors';

import { BillingStateActions } from './billing-state.actions';

@Injectable()
export class BillingAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly billingV3Service = inject(BillingV3Service);

  trackOpenPricingPlansModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.openPricingPlansModal),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.PRICING_MODAL_OPENED }))
    )
  );

  trackGettingStripeCheckoutUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getCheckoutUrl),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.CHECKOUT_STARTED }))
    )
  );

  /** right now, this will fire even if going from trialing or free */
  trackUpgrading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.getUpdatedSubscriptionAfterPlanChangeSuccess),
      /** if config ids match, we know we are upgrading */
      filter(({ subscription, configId }) => subscription.subscriptionConfigurationId === configId),
      switchMap(({ subscription, configId }) =>
        this.billingV3Service.getSubscriptionConfigById(configId).pipe(
          map(({ name, frequency }) =>
            SegmentActions.track({
              event: SegmentTrackEvent.PLAN_UPGRADED,
              params: { subscriptionStartDate: subscription.subscriptionStartDate, planName: name, frequency },
            })
          )
        )
      )
    )
  );

  trackPlanDowngradingOrCanceling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.getUpdatedSubscriptionAfterPlanChangeSuccess),
      /** if config ids don't match, we know we are downgrading since upgrading takes effect immediately */
      filter(({ subscription, configId }) => subscription.subscriptionConfigurationId !== configId),
      switchMap(({ subscription, configId }) =>
        this.billingV3Service.getSubscriptionConfigById(configId).pipe(
          map(({ name, frequency, planLevel }) =>
            planLevel === PlanLevel.Freemium
              ? SegmentActions.track({
                  event: SegmentTrackEvent.PAID_PLAN_CANCELED,
                  params: { planName: name },
                })
              : SegmentActions.track({
                  event: SegmentTrackEvent.PAID_PLAN_DOWNGRADED,
                  params: { subscriptionStartDate: subscription.subscriptionStartDate, planName: name, frequency },
                })
          )
        )
      )
    )
  );

  trackDowngradeRetentionInitiated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.openRetentionDialog),
      /** downgrade and cancel retention dialogs are opened from the same action */
      filter(({ data }) => !data.toFree),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.DOWNGRADE_RETENTION_INITIATED }))
    )
  );

  trackCancelRetentionInitiated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.openRetentionDialog),
      /** downgrade and cancel retention dialogs are opened from the same action */
      filter(({ data }) => data.toFree),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.CANCEL_RETENTION_INITIATED }))
    )
  );

  trackCancellationReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.recordCancellationReason),
      map(({ reasons }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.CANCELLATION_REASON,
          params: {
            reason: reasons.reason,
            cancellationMoreInfo: reasons.cancellationMoreInfo,
            ...reasons.surveyAnswers,
          },
        })
      )
    )
  );

  trackTrialStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.registerCompany),
      concatLatestFrom(() => [
        this.store.select(selectCurrentUserAsCompanyUser),
        this.store.select(selectSubscription),
      ]),
      filter(([, currentCompanyUser]) => currentCompanyUser?.company?.billingType === BillingTypes.BillingV2),
      map(([, , subscription]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TRIAL_STARTED,
          params: {
            subscriptionPlan: subscription?.planId,
            trialStartDate: subscription?.trialStart,
            trialEndDate: subscription?.trialEnd,
            paymentAddedDate: new Date().toISOString(),
          },
        })
      )
    )
  );

  trackPaymentMethodAdded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.triggerPaymentAddedSegmentEvent),
      concatLatestFrom(() => this.store.select(selectSubscription)),
      map(([, subscription]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.PAYMENT_ADDED,
          params: {
            subscriptionPlan: subscription?.planId,
            trialStartDate: subscription?.trialStart,
            trialEndDate: subscription?.trialEnd,
            paymentAddedDate: new Date().toISOString(),
          },
        })
      )
    )
  );

  trackSubscriptionCancelled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.triggerSubscriptionCancelledSegmentEvent),
      concatLatestFrom(() => this.store.select(selectSubscription)),
      map(([, subscription]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.SUBSCRIPTION_CANCELLED,
          params: {
            subscriptionPlan: subscription?.planId,
            cancellationDate: new Date().toISOString(),
            effectiveUntilDate: subscription?.currentPeriodEnd,
          },
        })
      )
    )
  );

  trackRenewalBannerCtaClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.renewalNotificationCTAClicked),
      map(({ notificationType, daysUntilRenewal }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.RENEWAL_BANNER_CTA_CLICKED,
          params: { notificationType, daysUntilRenewal },
        })
      )
    )
  );

  trackRenewalBannerDismissed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.dismissRenewalNotification),
      map(({ notificationType, daysUntilRenewal }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.RENEWAL_BANNER_DISMISSED,
          params: { notificationType, daysUntilRenewal },
        })
      )
    )
  );
}
